import { setMinutes } from 'date-fns';
import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentReference, Query } from '@angular/fire/firestore';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError, Timestamp, zip } from 'rxjs';
import { catchError, defaultIfEmpty, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Brand } from '@common/interfaces/brand.interface';
import { Product, ProductPreFormat } from '@common/models/product.interface';
import { GruEje } from '@common/interfaces/grueje.interface';
import { AxisService } from '../axis/axis.service';
import { UserProviderService } from '../providers/user-provider.service';
import { BrandService } from '../brand/brand.service';
import { firestoreMaxClauses } from '@common/const/firebase-max-clauses.const';
import { ProductTypes } from '@common/enums/product-types.enum';
import { Tier } from '@common/types/tier.type';
import 'firebase/firestore';
import { ProductsPP } from '@common/models/products-pp.model';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  public static readonly collectionName = 'ARTICULOS';
  public static readonly collectionPPName = 'ARTICULOS_PP';
  brandList:number[];
  private mesesAntiguedadArticulosDeBaja: number = 12;
  private mesesAntiguedadArticulosDeBajaZZTT: number = 12;
  // Meses de antigüedad para productos de fidelidad - permite productos con baja inferior a un año
  private mesesAntiguedadArticulosDeBajaFidelidad: number = 12;
  private isZZTT: boolean = false;
  
  // Cache for brand list to avoid refetching on every search
  private cachedBrands$: Observable<Brand[]> | null = null;
  
  // Simple cache for search results (5 seconds TTL)
  private searchCache = new Map<string, { results: Observable<Product[]>, timestamp: number }>();
  private readonly CACHE_TTL = 5000; // 5 seconds
  
  constructor(
    private af: AngularFirestore,
    private translate: TranslateService,
    private axisService: AxisService,
    private userService: UserProviderService,
    private brandService: BrandService
  ) { }

  public getBy(
    filter: string,
    options?: {
      limit?: number;
      checkFidelity?: boolean;
      currentBrandsOnly?: boolean;
      defaultBrandOnly?: boolean;
      mapProduct?: boolean;
      idTier?: Tier;
    },
    filterWithPoints1000?: boolean,
    unsubscribeProduct?: boolean,
    zztt?: boolean
  ): Observable<Product[]> {
    zztt && (this.isZZTT = true);
    
    // Create cache key
    const cacheKey = JSON.stringify({ filter, options, filterWithPoints1000, unsubscribeProduct, zztt });
    
    // Check cache first
    const cached = this.searchCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      return cached.results;
    }
    
    // Use cached brand list or create new one with shareReplay
    if (!this.cachedBrands$) {
      this.cachedBrands$ = this.brandService.getAllActiveBrands().pipe(
        shareReplay(1)
      );
    }
    const brands$ = this.cachedBrands$;
    
    const fidelity$ = options?.checkFidelity
      ? this.userService.getDefaultStand().pipe(
          map(stand => stand.PermiteArticulosSinPuntos === false),
          shareReplay(1)
        )
      : of(undefined as boolean);

    const result$ = brands$.pipe(
      take(1),
      tap( brands => {
        this.brandList = brands.map(brands => {
          const datoUsuarioDiv = JSON.parse(localStorage.getItem('userDiv'));
          const idDiv = datoUsuarioDiv.roleData.ID_DIVISION;

          if (brands.ID_DIVISION == idDiv) {
            return brands.ID_MARCA;
          }
        })
      }),
      switchMap(brands =>
        zip(
          this.af.collection<ProductPreFormat>(
            ProductsService.collectionName,
            ref => {
              const filterFormatted = filter.trim().toUpperCase();
              const queryFn = (orderBy: string, searchTerm: string) => {
                let query = ref
                  .orderBy(orderBy)
                  .orderBy('FECHA_BAJA_ARTICULO')
                  .startAt(searchTerm)
                  .endAt(searchTerm + '\uf8ff') as Query<ProductPreFormat>;
                return query;
              };

              switch (this.filterIs(filterFormatted)) {
                case 'eanCode':
                  return queryFn('CODIGO_EAN', filterFormatted);
                case 'description':
                  return queryFn('DESCRIPCION_ARTICULO', filterFormatted);
                case 'type':
                  return queryFn('TIPO_ARTICULO', filterFormatted);
              }
            }
          ).valueChanges(),
          fidelity$.pipe(take(1)),
          of(this.brandList)
        )
      ),
      // Combine all filtering operations into a single pass for better performance
      map(([ products, fidelityProductsOnly, brands]) => {
        const filterUpper = filter.trim().toUpperCase();
        const filterType = this.filterIs(filterUpper);
        const limit = options?.limit !== null ? (options?.limit ?? 20) : Number.MAX_SAFE_INTEGER;
        
        // Apply all filters in a single pass
        const filteredProducts = products.filter(p => {
          // Filter 1: Case-insensitive description search
          if (filterType === 'description' && !p.DESCRIPCION_ARTICULO?.toUpperCase().includes(filterUpper)) {
            return false;
          }
          
          // Filter 2: Brand filtering
          if (!brands.includes(p.ID_MARCA)) {
            return false;
          }
          
          // Filter 3: Fidelity and points filtering
          if (fidelityProductsOnly && (
            p.PUNTOS_ARTICULO <= 0 || 
            (filterWithPoints1000 !== true && p.PUNTOS_ARTICULO === 10000)
          )) {
            return false;
          }
          
          // Filter 4: MGM filtering
          if (p.MGM) {
            return false;
          }
          
          return true;
        }).slice(0, limit);
        
        return { products: filteredProducts, fidelityProductsOnly };
      }),
      switchMap(({ products, fidelityProductsOnly }) =>
        zip(
          ...products.map(product =>
            (options.mapProduct
              ? this.productMapper(product, undefined, undefined, undefined, unsubscribeProduct)
              : this.productBasicMapper(product, unsubscribeProduct, false, fidelityProductsOnly)
            ).pipe(
              // Only throw error if only one product is found
              // In array of products just filter those with error
              catchError(err => products.length === 1 ? throwError(err) : of(null))
            )
          )
        ).pipe(
          defaultIfEmpty<Product[]>([]),
          map(ps => ps.filter(p => !!p))
        )
      ),
      // Cache the result with shareReplay
      shareReplay(1),
      // Add to cache
      tap(results => {
        this.searchCache.set(cacheKey, { results: of(results), timestamp: Date.now() });
        // Clean old cache entries (keep only last 50)
        if (this.searchCache.size > 50) {
          const oldestKey = this.searchCache.keys().next().value;
          this.searchCache.delete(oldestKey);
        }
      })
    );
    
    return result$;
  }

  /**
   * Clear the search cache and brand cache
   * Call this when products or brands are updated
   */
  public clearCache(): void {
    this.searchCache.clear();
    this.cachedBrands$ = null;
  }

  public getByEAN(
    eanCode: string,
    matchingBrands?: Brand[],
    fidelity?: boolean,
    prospect?: boolean,
    unsubscribeProduct?: boolean,
    showAllProducts: boolean = false
  ): Observable<Product> {
    return this.af.collection<ProductPreFormat>(
      ProductsService.collectionName,
      ref => ref.where('CODIGO_EAN', '==', eanCode?.trim()).limit(1)
    ).valueChanges().pipe(
      switchMap(({ 0: product }) => this.productMapper(product, matchingBrands, fidelity, prospect, unsubscribeProduct, showAllProducts))
    );
  }

  public getArticleMgmByEAN(
    eanCode: string,
  ): Observable<Product> {
    return this.af.collection<ProductPreFormat>(
      ProductsService.collectionName,
      ref => ref.where('CODIGO_EAN', '==', eanCode?.trim()).limit(1)
    ).get().pipe(
      map(snapshot => snapshot.docs.map(d => d.data())),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  public getProductByRef(ref: DocumentReference<Product>, allowDiscontinued: boolean = true): Observable<Product | null> {
    return this.af.doc<ProductPreFormat>(ref).valueChanges().pipe(
      switchMap(p => this.productMapper(p, undefined, undefined, undefined, allowDiscontinued, allowDiscontinued)),
      catchError(error => {
        // Log the error but return null to allow the list to continue loading
        console.warn('Product unavailable or discontinued:', error);
        return of(null);
      })
    );
  }

  public getProductRefByEan(eanCode: string): DocumentReference<Product> {
    return this.af.doc<Product>(`${ProductsService.collectionName}/${eanCode}`).ref;
  }

  private productMapper(
    product: ProductPreFormat,
    matchingBrands?: Brand[],
    fidelity?: boolean,
    prospect?: boolean,
    unsubscribeProduct?: boolean,
    showAllProducts?: boolean
  ): Observable<Product> {

    //console.log("productMapper", product)

    if (matchingBrands?.length && !matchingBrands.map(({ ID_MARCA }) => ID_MARCA).includes(product.ID_MARCA)) {
      return throwError(this.translate.instant('Products.Errors.NoMatchingBrands'));
    }

    if (fidelity && !product.PUNTOS_ARTICULO) {
      return throwError(this.translate.instant('Products.Errors.NoFidelity'));
    }

    if (prospect && product.TIPO_ARTICULO !== 'MU') {
      return throwError(this.translate.instant('Products.Errors.NoProspect'));
    }

    return this.productBasicMapper(product, unsubscribeProduct, showAllProducts, fidelity).pipe(
      switchMap(p =>
        zip(
          p.CODIGO_EJE >= 0
            ? this.axisService.getAxis({
                brand: p.ID_MARCA,
                axis: p.CODIGO_EJE
              })
            : of(undefined as GruEje),
          p.SUBEJE
            ? this.axisService.getAxis({
                brand: p.ID_MARCA,
                axis: p.CODIGO_EJE,
                subaxis: (p.SUBEJE as unknown) as string
              })
            : of(undefined as GruEje),
          p.LINEA
            ? this.axisService.getAxis({
                brand: p.ID_MARCA,
                axis: p.CODIGO_EJE,
                subaxis: (p.SUBEJE as unknown) as string,
                line: (p.LINEA as unknown) as string
              })
            : of(undefined as GruEje),
          this.af.doc<Brand>(p.Ref_ID_MARCA).valueChanges(),
        ).pipe(
          map(([axis, subaxis, line, brand]) => ({
            ...p,
            MARCA: brand,
            EJE: axis,
            SUBEJE: subaxis,
            LINEA: line
          } as Product))
        )
      )
    );
  }

  public getEanProductPP(idPP):Observable<ProductsPP[]> {
    return this.af
    .collection<ProductsPP>(
      ProductsService.collectionPPName,
      query => query
        .where('ID_PP', '==', idPP)
        .limit(1)
    ).get().pipe(
      map(snapshot => snapshot.docs.map(d => d.data())),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  private productBasicMapper(product: ProductPreFormat, unsubscribeProduct?: boolean, showAllProducts: boolean = false, fidelity?: boolean): Observable<Product> {

    if (!product) {
      return throwError(this.translate.instant('Products.Errors.NotFound'));
    }
  
    if (product.FECHA_BAJA_ARTICULO) {
      if (!unsubscribeProduct) {
        return throwError(this.translate.instant('Products.Errors.Unavailable'));
      }
  
      const currentDate = new Date();
      
      // Usar lógica específica para productos de fidelidad
      // Permite productos de fidelidad cuya baja sea inferior a un año de antigüedad
      let monthsToSubtract: number;
      if (fidelity) {
        monthsToSubtract = this.mesesAntiguedadArticulosDeBajaFidelidad;
      } else {
        monthsToSubtract = this.isZZTT ? this.mesesAntiguedadArticulosDeBajaZZTT : this.mesesAntiguedadArticulosDeBaja;
      }
      
      const deadlineDate = new Date();
      deadlineDate.setMonth(currentDate.getMonth() - monthsToSubtract);
      deadlineDate.setHours(0, 0, 0, 0);
  
      const bajaDate = product.FECHA_BAJA_ARTICULO.toDate();
      if (showAllProducts || bajaDate >= deadlineDate) {
        return of({
          ...product,
          FECHA_ALTA_ARTICULO: product.FECHA_ALTA_ARTICULO?.toDate(),
          FECHA_BAJA_ARTICULO: bajaDate,
        });
      } else {
        return throwError(this.translate.instant('Products.Errors.Unavailable'));
      }
    }
  
    return of({
      ...product,
      FECHA_ALTA_ARTICULO: product.FECHA_ALTA_ARTICULO?.toDate(),
      FECHA_BAJA_ARTICULO: product.FECHA_BAJA_ARTICULO?.toDate(),
    });
  }

  private filterIs(filter: string): 'eanCode' | 'description' | 'type' | null {
    return filter
      // Contains only digits
      ? /^\d+$/.test(filter)
        ? 'eanCode'
        : Object.values(ProductTypes).includes(filter as ProductTypes)
          ? 'type'
          : 'description'
      : null;
  }

  /**
   * Gets the effective CODIGO_EJE for a product
   * Returns CODIGO_EJE_AGRUPACION if it exists, otherwise returns CODIGO_EJE
   * This should be used throughout the app when accessing a product's axis code
   */
  public static getEffectiveCodigoEje(product: Product): number {
    return product.CODIGO_EJE_AGRUPACION ?? product.CODIGO_EJE;
  }
}
